import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import ReportPage from "@/pages/ReportPage";
import MyReportsPage from "@/pages/MyReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SharedReportPage from "@/pages/SharedReportPage";
import ComparePage from "@/pages/ComparePage";
import SupportPage from "@/pages/SupportPage";
import DealCalculatorsPage from "@/pages/DealCalculatorsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults to include auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem("authToken");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, {
      withCredentials: true
    });
    localStorage.setItem("authToken", response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name }, {
      withCredentials: true
    });
    localStorage.setItem("authToken", response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("authToken");
    setUser(null);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return { user, setUser, loading, login, register, logout, googleLogin, checkAuth };
};

// Auth Callback Component
const AuthCallback = ({ setUser }) => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.get(`${API}/auth/session?session_id=${sessionId}`, {
            withCredentials: true
          });
          // Store token in localStorage for mobile compatibility
          if (response.data.token) {
            localStorage.setItem("authToken", response.data.token);
          }
          setUser(response.data.user || response.data);
          navigate('/dashboard', { replace: true, state: { user: response.data.user || response.data } });
        } catch (error) {
          console.error("Auth callback error:", error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/dashboard', { replace: true });
      }
    };

    processSession();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-center">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, user, loading }) => {
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login with the current path as redirect parameter
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();
  const auth = useAuth();
  
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={auth.setUser} />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        auth.user ? <Navigate to="/dashboard" replace /> : <LoginPage auth={auth} />
      } />
      <Route path="/register" element={
        auth.user ? <Navigate to="/dashboard" replace /> : <RegisterPage auth={auth} />
      } />
      <Route path="/shared/:shareId" element={<SharedReportPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <DashboardPage auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/report/:reportId" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <ReportPage auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <MyReportsPage auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <SettingsPage auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <SubscriptionPage auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <SubscriptionPage auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/compare" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <ComparePage auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/calculators" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <DealCalculatorsPage auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/support" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <SupportPage auth={auth} />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
